import { Link } from "wouter";
import { ArrowRight, Link2 } from "lucide-react";
import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { usePageTitle } from "@/hooks/use-page-title";

const PADSPLIT_CONNECT_PATH = "/app/padsplit-sync";
const ADD_PROPERTY_PATH = "/app/properties?add=1";

/**
 * First-run screen after signup (and after a lifetime purchase): one job —
 * get the host's PadSplit account connected. No plan picker, billing or card
 * form here; billing lives on /app/account/billing.
 */
export default function AppConnectPadsplit() {
  usePageTitle("Connect your PadSplit account");
  const lifetimePurchased =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("lifetime") === "purchased";

  return (
    <AppLayout>
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="mb-4 flex justify-end gap-2">
          <ExplainerVideoButton pageKey="connect-padsplit" />
          <GuideButton pageKey="connect-padsplit" />
        </div>
        <Card className="p-6 sm:p-8 space-y-5" data-tour="connect-padsplit-card">
          {lifetimePurchased && (
            <p
              className="rounded-md border border-brand/30 bg-brand-subtle px-3 py-2 text-sm text-brand"
              data-testid="text-connect-lifetime-purchased"
            >
              Returning from checkout does not confirm activation. Verify your payment before continuing.
            </p>
          )}
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10">
            <Link2 className="h-6 w-6 text-brand" aria-hidden="true" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold" data-testid="text-connect-padsplit-heading" data-tour="connect-padsplit-heading">
              Connect your PadSplit account
            </h1>
            <p className="text-muted-foreground" data-testid="text-connect-padsplit-body">
              PadPulse connects to your PadSplit account to bring in your properties, rooms, members and
              messages, then keeps them up to date for you. It takes a couple of minutes, and you can start
              using PadPulse as soon as your first sync finishes.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link href={PADSPLIT_CONNECT_PATH}>
              <Button
                className="w-full bg-brand hover:bg-brand-hover text-white sm:w-auto"
                data-testid="button-connect-padsplit"
                data-tour="connect-padsplit-primary"
              >
                Connect PadSplit
                <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
              </Button>
            </Link>
            <Link
              href={ADD_PROPERTY_PATH}
              className="text-sm font-medium text-brand underline-offset-4 hover:underline"
              data-testid="link-connect-add-property"
            >
              My rooms aren't on PadSplit → Add a property
            </Link>
          </div>
        </Card>
      </div>
    </AppLayout>
  );
}
